import React, { useEffect, useState } from 'react';
import { api } from '../../lib/api';
import { useSettings } from '../../lib/settings';
import { Button } from '../../components/ui/button';
import { useToast } from '../../hooks/use-toast';
import { Save, Phone, Mail, MapPin, Clock, Facebook, Twitter, Linkedin, Youtube, Instagram, Building2, Bell } from 'lucide-react';

function Field({ label, icon: Icon, ...props }) {
  return (
    <div>
      <label className="text-xs font-bold text-gray-500 mb-1 block">{label}</label>
      <div className="relative">
        {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />}
        <input {...props} className={`w-full ${Icon ? 'pl-9' : 'pl-3'} pr-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]`} />
      </div>
    </div>
  );
}

export default function AdminContent() {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();
  const { refresh } = useSettings();

  useEffect(() => { api.settings().then(setForm).catch(() => {}); }, []);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await api.admin.settingsUpdate({
        contactPhone: form.contactPhone,
        contactEmail: form.contactEmail,
        contactAddress: form.contactAddress,
        contactHours: form.contactHours,
        footerAbout: form.footerAbout,
        footerCopyright: form.footerCopyright,
        socialFacebook: form.socialFacebook,
        socialTwitter: form.socialTwitter,
        socialLinkedin: form.socialLinkedin,
        socialYoutube: form.socialYoutube,
        socialInstagram: form.socialInstagram,
        hiringCompanies: form.hiringCompanies,
        notifyEmail: form.notifyEmail,
      });
      toast({ title: 'Content saved' });
      refresh();
    } catch { toast({ title: 'Failed', variant: 'destructive' }); }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-extrabold">Site content</h1>
        <p className="text-gray-500 text-sm mt-1">Edit contact info, footer, social links and homepage strip. Changes appear across the entire site.</p>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-4">
        <h2 className="font-bold text-lg flex items-center gap-2"><Phone size={18} className="text-[#6b47dc]" /> Contact information</h2>
        <p className="text-xs text-gray-500 -mt-3">Shown on the Contact page and in the footer.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Phone" icon={Phone} value={form.contactPhone || ''} onChange={set('contactPhone')} placeholder="+91 98765 43210" />
          <Field label="Support email" icon={Mail} value={form.contactEmail || ''} onChange={set('contactEmail')} placeholder="support@skillbridge.guru" />
          <Field label="Address" icon={MapPin} value={form.contactAddress || ''} onChange={set('contactAddress')} placeholder="Bengaluru, India" />
          <Field label="Hours" icon={Clock} value={form.contactHours || ''} onChange={set('contactHours')} placeholder="Mon-Sun, 24x7" />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block flex items-center gap-1"><Bell size={12} /> Notification email (where contact form + signup + enrollment alerts go)</label>
          <input value={form.notifyEmail || ''} onChange={set('notifyEmail')} placeholder="admin@yourbusiness.com" className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
          <p className="text-[11px] text-gray-500 mt-1">Requires SMTP to be configured &amp; enabled.</p>
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-4">
        <h2 className="font-bold text-lg flex items-center gap-2"><Building2 size={18} className="text-[#6b47dc]" /> Footer content</h2>
        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block">Company description</label>
          <textarea rows={3} value={form.footerAbout || ''} onChange={set('footerAbout')} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
        </div>
        <div>
          <label className="text-xs font-bold text-gray-500 mb-1 block">Copyright line</label>
          <input value={form.footerCopyright || ''} onChange={set('footerCopyright')} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-4">
        <h2 className="font-bold text-lg">Social links</h2>
        <p className="text-xs text-gray-500 -mt-3">Paste full URLs. Leave blank or type <span className="font-mono">#</span> to disable.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Facebook" icon={Facebook} value={form.socialFacebook || ''} onChange={set('socialFacebook')} />
          <Field label="Twitter / X" icon={Twitter} value={form.socialTwitter || ''} onChange={set('socialTwitter')} />
          <Field label="LinkedIn" icon={Linkedin} value={form.socialLinkedin || ''} onChange={set('socialLinkedin')} />
          <Field label="YouTube" icon={Youtube} value={form.socialYoutube || ''} onChange={set('socialYoutube')} />
          <Field label="Instagram" icon={Instagram} value={form.socialInstagram || ''} onChange={set('socialInstagram')} />
        </div>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-gray-200 space-y-3">
        <h2 className="font-bold text-lg">Homepage &mdash; Hiring companies strip</h2>
        <p className="text-xs text-gray-500">Comma-separated company names shown in the marquee strip. Example: <span className="font-mono">Google, Amazon, Microsoft</span></p>
        <textarea rows={2} value={form.hiringCompanies || ''} onChange={set('hiringCompanies')} className="w-full px-3 py-2 rounded-lg border border-gray-200 outline-none focus:border-[#6b47dc]" />
      </div>

      <div className="sticky bottom-4">
        <Button onClick={save} disabled={saving} className="bg-[#6b47dc] hover:bg-[#5a3ac0] shadow-2xl h-12 px-6">
          <Save size={16} className="mr-1.5" /> {saving ? 'Saving...' : 'Save all content'}
        </Button>
      </div>
    </div>
  );
}
